"use client";

import { authClient, signOut } from "@/lib/auth-client";
import { capturePosthogEvent, resetPosthog } from "@/lib/posthog-browser";

/**
 * Ends impersonation if active (restores admin session).
 * @returns true when an impersonation session was stopped (caller should not full sign-out).
 */
async function stopImpersonationIfActive(): Promise<boolean> {
	const { data: session } = await authClient.getSession();
	const impersonatedBy = (
		session as { session?: { impersonatedBy?: string | null } } | null | undefined
	)?.session?.impersonatedBy;
	if (!impersonatedBy) {
		return false;
	}
	const res = await fetch("/api/admin/stop-impersonating", {
		method: "POST",
		credentials: "include",
	});
	if (!res.ok) {
		const body = (await res.json().catch(() => null)) as { error?: string } | null;
		throw new Error(body?.error ?? "Failed to stop impersonation");
	}
	await authClient.getSession();
	return true;
}

/** Clears the Better Auth session and analytics identity. */
export async function performClientLogout(): Promise<void> {
	const wasImpersonating = await stopImpersonationIfActive();
	if (wasImpersonating) {
		// Restore admin session instead of destroying it mid-impersonation.
		return;
	}
	await capturePosthogEvent("user_logged_out");
	await resetPosthog();
	await signOut();
	// Drop any cached session snapshot so the next page load cannot treat us as signed in.
	await authClient.getSession();
}

/** Ends the session and hard-navigates to login (avoids stale client session races). */
export async function performClientLogoutAndRedirectToLogin(): Promise<void> {
	const wasImpersonating = await stopImpersonationIfActive();
	if (wasImpersonating) {
		window.location.assign("/");
		return;
	}
	await capturePosthogEvent("user_logged_out");
	await resetPosthog();
	await signOut();
	await authClient.getSession();
	window.location.assign("/login");
}
